import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { SectionContainer } from "@/components/marketing/SectionContainer";
import { CtaBand } from "@/components/marketing/CtaBand";
import {
  CATEGORIES,
  rolesByCategory,
  type RoleCategory,
} from "@/app/marketing/constants/rolesCatalog";
import { usePageMeta } from "./usePageMeta";

export function Features() {
  usePageMeta({
    title: "Features — the virtual employee catalog",
    description:
      "Browse every virtual role on VirtualDesk.ai: engineers, executive assistants, customer support, SDRs, analysts, and more. Each one runs scoped, audit-logged, and human-approved.",
  });
  return (
    <>
      <SectionContainer>
        <div className="grid lg:grid-cols-[240px_1fr] gap-10">
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <p className="text-xs font-semibold uppercase tracking-wider text-brand-ink/50">
              Jump to
            </p>
            <nav className="mt-3 space-y-1" aria-label="Feature categories">
              {CATEGORIES.map((c) => (
                <a
                  key={c.id}
                  href={`#${c.id}`}
                  className="block rounded-md px-3 py-2 text-sm text-brand-ink/75 hover:bg-white/5 hover:text-brand-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-violet"
                >
                  {c.label}
                </a>
              ))}
            </nav>
            <div className="mt-8 hidden lg:block">
              <Button asChild variant="primary" size="sm">
                <Link to="/register">Get started</Link>
              </Button>
            </div>
          </aside>

          <div>
            <div className="max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-wider text-brand-violet">
                Features
              </p>
              <h1 className="mt-3 font-display text-5xl md:text-6xl lg:text-7xl tracking-wide leading-[0.95] text-brand-ink">
                The virtual employee catalog.
              </h1>
              <p className="mt-6 text-lg text-brand-ink/70 leading-relaxed">
                Twelve-plus curated roles, grouped by what they do. Each runs in its own sandbox,
                with scoped tool permissions, human approval gates, and a full audit trail.
              </p>
            </div>

            <div className="mt-16 space-y-20">
              {CATEGORIES.map((cat) => (
                <CategorySection key={cat.id} id={cat.id} label={cat.label} blurb={cat.blurb} />
              ))}
            </div>
          </div>
        </div>
      </SectionContainer>

      <CtaBand
        heading="Don't see a role you need?"
        subheading="Enterprise plans include custom agents built for your workflow. Tell us what you need."
        primaryLabel="Talk to us"
        primaryHref="/contact"
      />
    </>
  );
}

function CategorySection({
  id,
  label,
  blurb,
}: {
  id: RoleCategory;
  label: string;
  blurb: string;
}) {
  const roles = rolesByCategory(id);
  return (
    <section id={id} className="scroll-mt-24">
      <div className="pb-6 border-b border-white/10">
        <h2 className="font-display text-4xl md:text-5xl tracking-wide text-brand-ink">{label}</h2>
        <p className="mt-3 text-base text-brand-ink/70 max-w-2xl">{blurb}</p>
      </div>

      <div className="mt-10 space-y-10">
        {roles.map((r) => {
          const Icon = r.icon;
          return (
            <article
              key={r.code}
              className="grid md:grid-cols-[auto_1fr_auto] gap-6 md:gap-8 items-start rounded-xl border border-white/10 bg-white/5 p-6 md:p-8"
            >
              <div className="rounded-lg bg-brand-violet/15 p-3 text-brand-violet w-fit">
                <Icon className="h-8 w-8" />
              </div>

              <div className="min-w-0">
                <div className="flex flex-wrap items-baseline gap-3">
                  <h3 className="font-display text-3xl tracking-wide text-brand-ink">{r.name}</h3>
                  <span className="text-sm font-mono text-brand-cyan">
                    from {r.startingPrice}
                  </span>
                </div>
                <p className="mt-3 text-base text-brand-ink/75 leading-relaxed">{r.description}</p>

                <div className="mt-5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-brand-ink/50">
                    Capabilities
                  </p>
                  <ul className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2">
                    {r.capabilities.slice(0, 5).map((c) => (
                      <li key={c} className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-brand-violet shrink-0 mt-0.5" />
                        <span className="text-sm text-brand-ink/85">{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-brand-ink/50">
                    Supported tools
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {r.tools.map((t) => (
                      <span
                        key={t}
                        className="inline-flex items-center rounded-md border border-white/10 bg-white/5 px-2.5 py-1 text-xs font-medium text-brand-ink/80"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-6">
                  <p className="text-xs font-semibold uppercase tracking-wider text-brand-ink/50">
                    Example workflows
                  </p>
                  <div className="mt-3 grid sm:grid-cols-2 gap-3">
                    {r.workflows.map((w) => (
                      <div
                        key={w.title}
                        className="rounded-lg border border-white/10 bg-brand-midnight/60 p-4"
                      >
                        <p className="text-sm font-semibold text-brand-ink">{w.title}</p>
                        <p className="mt-1.5 text-xs text-brand-ink/65 leading-relaxed">
                          {w.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="md:self-center">
                <Button asChild variant="outline" className="border-white/20 text-brand-ink hover:bg-white/5">
                  <Link to={`/roles/${r.code}`}>
                    Details
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
